import { Card } from "@/components/ui/card";
import { TrendingUp, Link2, Users, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const features = [
  {
    icon: TrendingUp,
    title: "Real Websites with Real Traffic",
    description: "Every website we work with has verified organic traffic from Ahrefs or SEMrush. We never use private blog networks, link farms, or spam sites. Your backlinks come from legitimate websites that real people actually visit and engage with.",
  },
  {
    icon: Link2,
    title: "Industry-Relevant Placements",
    description: "We manually research and reach out to websites in your specific industry. Whether you're in SaaS, ecommerce, finance, health, or any other niche, we find placements on sites that your target audience actually reads and trusts.",
  },
  {
    icon: Users,
    title: "White-Hat Strategies Only",
    description: "We follow Google's webmaster guidelines to the letter. No link schemes, no paid links disguised as editorial, no risky tactics that could get your site penalized. Just sustainable link building strategies that stand the test of algorithm updates.",
  },
];

const About = () => {
  return (
    <section className="py-16 md:py-24 bg-muted/50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3">
              Quality Over Quantity
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-6 md:mb-8">
              Link Building That <br className="hidden sm:block" />
              Actually Moves the Needle
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-4 leading-relaxed">
              Here's the truth: quality link building is still one of the best ways to climb Google's rankings. In a world where AI is changing search, backlinks remain a core ranking factor that Google uses to determine which websites deserve to rank at the top.
            </p>
            <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8 leading-relaxed">
              When you do it right, the results speak for themselves: more organic traffic, better keyword rankings, and a stronger online presence that compounds over time. We're not chasing quick wins here. We measure success in months and years, not days and weeks.
            </p>
            
            <div className="space-y-4 md:space-y-6">
              {features.map((feature, index) => (
                <Card key={index} className="p-5 md:p-6 hover:shadow-lg transition-shadow active:scale-[0.98]">
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="w-11 h-11 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base md:text-lg font-semibold text-foreground mb-1.5 md:mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          <div className="relative mt-8 lg:mt-0">
            <div className="bg-primary/5 rounded-2xl md:rounded-3xl p-6 md:p-12 border-2 border-primary/10 max-w-full">
              <div className="space-y-4 md:space-y-6">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-4 md:mb-6">
                  <Users className="w-6 h-6 md:w-7 md:h-7 text-primary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3 md:mb-4">
                  Who We Serve
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Most of our clients get it. They understand how SEO works and why quality backlinks matter for growth.
                </p>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  They know that real results take time and effort, especially when you are competing in tough markets. And they are okay with that because they want links that last.
                </p>
                <div className="pt-4 md:pt-6 border-t border-primary/10">
                  <p className="text-base md:text-lg font-semibold text-foreground italic mb-4">
                    "We do not just aim for excellence. We set the bar."
                  </p>
                  <Link to="/about" className="inline-flex items-center text-primary font-medium hover:underline mr-6">
                    Learn more about us <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                  <Link to="/b2b-link-building" className="inline-flex items-center text-accent font-medium hover:underline">
                    B2B Link Building <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
